import React from 'react';
import styled from 'styled-components';
import { Clock, CheckCircle2, AlertCircle, ChevronRight, UserCheck, Inbox, XCircle } from 'lucide-react';
import { ApplyCompanyUserDto } from '@/services/companyUserService';
import { 
  Section, SectionHeader, SectionTitleWrapper, SectionIcon, 
  SectionTitle, SectionSubtitle, SectionBadge, EmptyActiveSpaces, 
  EmptyIconWrapper, EmptyTitle, EmptyDescription, fadeIn, 
  LoadingState, LoadingSpinner 
} from '../../SharedStyles';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import Avatar from '@/common/components/avatar/Avatar';

interface ApplicationTabProps {
  pendingApplications: ApplyCompanyUserDto[];
  processedApplications: ApplyCompanyUserDto[];
  isLoadingApplications: boolean;
  handleProcessApplication: (notificationId: string) => void;
  formatDate: (dateString: string) => string;
  isExpired: (expiresAt: string) => boolean;
}

const ApplicationTab: React.FC<ApplicationTabProps> = ({
  pendingApplications,
  processedApplications,
  isLoadingApplications,
  handleProcessApplication,
  formatDate,
  isExpired
}) => {
  return (
    <>
      {/* 대기 중인 신청 */}
      <Section>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionIcon $variant="warning">
              <Clock size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>대기 중인 신청</SectionTitle>
              <SectionSubtitle>승인 또는 거절이 필요한 가입 신청입니다</SectionSubtitle>
            </div>
          </SectionTitleWrapper>
          {pendingApplications.length > 0 && (
            <SectionBadge>{pendingApplications.length}건</SectionBadge>
          )}
        </SectionHeader>

        {isLoadingApplications ? (
          <LoadingState>
            <LoadingSpinner />
            <span>가입 신청 목록을 불러오는 중...</span>
          </LoadingState>
        ) : pendingApplications.length > 0 ? (
          <TableWrapper>
            <TableHeader>
              <HeaderCell $flex={1}>신청자</HeaderCell>
              <HeaderCell $width="100px">상태</HeaderCell>
              <HeaderCell $width="160px">만료일</HeaderCell>
              <HeaderCell $width="140px">액션</HeaderCell>
            </TableHeader>
            {pendingApplications.map((app, index) => (
              <TableRow key={app.notificationId} $delay={index * 0.03}>
                <RowCell $flex={1}>
                  <UserCellContent>
                    <Avatar alt={app.name || '사용자'} isUser={true} sizes="md" />
                    <UserTextInfo>
                      <UserName>{app.name || '이름 없음'}</UserName>
                      <UserEmail>{app.email || '이메일 없음'}</UserEmail>
                    </UserTextInfo>
                  </UserCellContent>
                </RowCell>
                <RowCell $width="100px">
                  <StatusBadge $status="pending">
                    <Clock size={12} />
                    대기 중
                  </StatusBadge>
                </RowCell>
                <RowCell $width="160px">
                  <ExpiryText $expired={isExpired(app.expiresAt)}>
                    <AlertCircle size={14} />
                    <span>
                      {isExpired(app.expiresAt) 
                        ? '만료됨' 
                        : formatDate(app.expiresAt)
                      }
                    </span>
                  </ExpiryText>
                </RowCell>
                <RowCell $width="140px">
                  <ProcessButton onClick={() => handleProcessApplication(app.notificationId)}>
                    <UserCheck size={14} />
                    <span>처리하기</span>
                    <ChevronRight size={14} />
                  </ProcessButton>
                </RowCell>
              </TableRow>
            ))}
          </TableWrapper>
        ) : (
          <EmptyActiveSpaces>
            <EmptyIconWrapper>
              <Inbox size={48} />
            </EmptyIconWrapper>
            <EmptyTitle>대기 중인 신청이 없습니다</EmptyTitle>
            <EmptyDescription>
              새로운 가입 신청이 있으면 여기에 표시됩니다.
            </EmptyDescription>
          </EmptyActiveSpaces>
        )}
      </Section>

      {/* 처리된 신청 */}
      {processedApplications.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionIcon $variant="success">
                <CheckCircle2 size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>처리된 신청</SectionTitle>
                <SectionSubtitle>이미 승인 또는 거절된 신청 내역입니다</SectionSubtitle>
              </div>
            </SectionTitleWrapper>
          </SectionHeader>

          <TableWrapper>
            <TableHeader>
              <HeaderCell $flex={1}>신청자</HeaderCell>
              <HeaderCell $width="100px">상태</HeaderCell>
              <HeaderCell $flex={1}>사유</HeaderCell>
            </TableHeader>
            {processedApplications.map((app, index) => (
              <TableRow key={app.notificationId} $delay={index * 0.03}>
                <RowCell $flex={1}>
                  <UserCellContent>
                    <Avatar alt={app.name || '사용자'} isUser={true} sizes="md" />
                    <UserTextInfo>
                      <UserName>{app.name || '이름 없음'}</UserName>
                      <UserEmail>{app.email || '이메일 없음'}</UserEmail>
                    </UserTextInfo>
                  </UserCellContent>
                </RowCell>
                <RowCell $width="100px">
                  <StatusBadge $status={app.isAccepted ? 'accepted' : 'rejected'}>
                    {app.isAccepted ? (
                      <>
                        <CheckCircle2 size={12} />
                        승인됨
                      </>
                    ) : (
                      <>
                        <XCircle size={12} />
                        거절됨
                      </>
                    )}
                  </StatusBadge>
                </RowCell>
                <RowCell $flex={1}>
                  {app.rejectReason ? (
                    <RejectReasonText>{app.rejectReason}</RejectReasonText>
                  ) : (
                    <NoReasonText>-</NoReasonText>
                  )}
                </RowCell>
              </TableRow>
            ))}
          </TableWrapper>
        </Section>
      )}
    </>
  );
};

const TableWrapper = styled.div`
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background: ${semanticColors.background.tertiary};
  border-bottom: 1px solid ${semanticColors.border.primary};
  gap: 1rem;
`;

const HeaderCell = styled.div<{ $flex?: number; $width?: string }>`
  ${props => props.$flex ? `flex: ${props.$flex};` : ''}
  ${props => props.$width ? `width: ${props.$width}; flex-shrink: 0;` : ''}
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.tertiary};
`;

const TableRow = styled.div<{ $delay: number }>`
  display: flex;
  align-items: center;
  padding: 0.875rem 1.25rem;
  gap: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.secondary};
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${semanticColors.background.tertiary};
  }
`;

const RowCell = styled.div<{ $flex?: number; $width?: string }>`
  ${props => props.$flex ? `flex: ${props.$flex}; min-width: 0;` : ''}
  ${props => props.$width ? `width: ${props.$width}; flex-shrink: 0;` : ''}
  display: flex;
  align-items: center;
`;

const UserCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

const UserTextInfo = styled.div`
  min-width: 0;
`;

const UserName = styled.p`
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${semanticColors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusBadge = styled.span<{ $status: 'pending' | 'accepted' | 'rejected' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2}
  font-weight: 600;
  white-space: nowrap;
  
  ${props => props.$status === 'pending' && `
    background: ${semanticColors.background.warning.secondary};
    color: ${semanticColors.text.onWarning.primary};
  `}
  
  ${props => props.$status === 'accepted' && `
    background: ${semanticColors.background.success.secondary};
    color: ${semanticColors.text.onSuccess.primary};
  `}
  
  ${props => props.$status === 'rejected' && `
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.secondary};
  `}
`;

const ExpiryText = styled.div<{ $expired: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.caption.caption1.default}
  color: ${props => props.$expired ? semanticColors.text.onDanger.primary : semanticColors.text.secondary};
  
  svg {
    color: ${props => props.$expired ? semanticColors.text.onDanger.primary : semanticColors.text.tertiary};
  }
`;

const ProcessButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }
`;

const RejectReasonText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.onDanger.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NoReasonText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

export default ApplicationTab;
